import React, { useCallback, useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { LuHandCoins, LuRefreshCw } from "react-icons/lu";
import { estimateInteractive } from "./interactiveItemService";

/* eslint-disable react/prop-types */

/**
 * Kredi tahmini düğmesi + popover.
 *
 * İnfografik sahne modalındaki kredi düğmesinin AYNISI (InfographicSceneModal,
 * `creditFooter`): aynı yerleşim, aynı ikon, aynı popover, aynı i18n
 * anahtarları. Bir süre burada kendi uydurduğum küçük bir rozet vardı —
 * kullanıcı aynı ürünün iki yerinde iki farklı kredi göstergesi görüyordu ve
 * bu, rakamın kendisinden önce güveni bozuyor.
 *
 * Fiyat sunucudan geliyor (`/interactive/generate-credit` → price_lists,
 * model başına satır). Burada hiçbir sayı hesaplanmıyor, hiçbir varsayılan
 * yok: bir fiyat satırı eksikse rakam gösterilmez, uydurulmaz.
 *
 * Tıklayınca sorar, otomatik değil — infografikteki davranış da bu, ve her
 * model değişiminde sunucuya gitmemek için doğru olan da bu.
 */
export default function InteractiveCreditButton({
  llmModel,
  disabled,
  label,
  // Hareketli Görsel'in "AI ile düzenle"si de bu düğmeyi kullanıyor; fiyatı
  // kendi uç noktasından gelir. Verilmezse interaktifin tahmini.
  estimate = estimateInteractive,
}) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  // Model değişince eski rakam yanlış olur: popover'ı kapat, veriyi at.
  useEffect(() => {
    setInfo(null);
    setOpen(false);
  }, [llmModel]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open]);

  const check = useCallback(async () => {
    if (info) {
      setOpen((v) => !v);
      return;
    }
    setLoading(true);
    try {
      const data = await estimate({ llmModel });
      setInfo(data);
      setOpen(true);
    } catch {
      // Tahmin alınamadıysa popover açılmaz. Üretim yine de çalışır — ücret
      // sunucuda atomik olarak kontrol ediliyor, burası yalnızca bilgi.
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [info, llmModel, estimate]);

  const credits = i18n.t("ImageHistory.Credits");

  return (
    <>
      <style>{`@keyframes ii-credit-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div ref={btnRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={check}
          disabled={loading || disabled}
          title={i18n.t("CreditEstimate.Tooltip")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "9px 16px",
            borderRadius: "12px",
            border: `1px solid ${open ? "#c4b5fd" : "#dce3f3"}`,
            background: open ? "#eef2ff" : "#f8fafc",
            color: open ? "#7c3aed" : "#64748b",
            fontSize: "14px",
            fontWeight: 600,
            cursor: loading ? "wait" : "pointer",
            transition: "background .18s ease, color .18s ease, border-color .18s ease",
            outline: "none",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? (
            <LuRefreshCw size={14} style={{ animation: "ii-credit-spin .8s linear infinite" }} />
          ) : (
            <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
          )}
          <span>
            {loading ? i18n.t("CreditEstimate.Loading") : i18n.t("CreditEstimate.Button")}
          </span>
        </button>

        {open && info && (
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 10px)",
              left: 0,
              background: "#fff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "16px 20px",
              minWidth: "270px",
              maxWidth: "340px",
              boxShadow: "0 4px 24px rgba(15,23,42,.12)",
              zIndex: 9999,
              fontSize: "13px",
              lineHeight: 1.6,
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: -7,
                left: 20,
                width: 13,
                height: 13,
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderTop: "none",
                borderLeft: "none",
                transform: "rotate(45deg)",
              }}
            />

            <div
              style={{
                fontWeight: 700,
                marginBottom: 12,
                fontSize: "14px",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
              {i18n.t("CreditEstimate.Title")}
            </div>

            {/* Sunucunun döndüğü satırlar — etiketi de fiyatı da o veriyor.
                `label` prop'u yalnızca onu ezmek isteyen çağıran için
                (düzenleme ekranı "İnteraktif öğe düzenlemesi" diyor). */}
            {(info.providers || []).map((p, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 12,
                  marginBottom: 7,
                }}
              >
                <span style={{ color: "#64748b", fontSize: "13px", flex: 1, lineHeight: 1.35 }}>
                  {label || p.label}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "13px",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {Number(p.cost).toFixed(2)}&nbsp;
                  <span style={{ color: "#8b95af", fontWeight: 400 }}>{credits}</span>
                </span>
              </div>
            ))}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
                paddingBottom: 14,
                borderBottom: "1px solid #f1f5f9",
                marginTop: 2,
              }}
            >
              <span style={{ color: "#64748b" }}>{i18n.t("CreditEstimate.Balance")}</span>
              <span style={{ fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                {Number(info.availableCredits || 0).toFixed(2)}&nbsp;
                <span style={{ color: "#8b95af", fontWeight: 400 }}>{credits}</span>
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                borderRadius: 8,
                background: info.hasSufficientBalance
                  ? "rgba(124,58,237,0.08)"
                  : "rgba(239,68,68,0.08)",
                color: info.hasSufficientBalance ? "#7c3aed" : "#ef4444",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              {info.hasSufficientBalance
                ? `✓ ${i18n.t("CreditEstimate.Sufficient")}`
                : `✗ ${i18n.t("CreditEstimate.Insufficient")}`}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
